import { maxWeightMatching, type WeightedEdge } from '../../matching/blossom.js';
import {
  allocateColoursWithRule,
  COLOUR_RULE_TEXT,
  colourState,
  type ColourRule,
} from '../colour.js';
import {
  PairingError,
  type Pair,
  type PairingEngine,
  type PairingRequest,
  type PairingResult,
} from '../types.js';
import { buildDutchPlayers, compatible, type DutchPlayer } from './state.js';

/**
 * FIDE (Dutch) System, C.04.3 as applied from 1 February 2026.
 *
 * The rules ask, bracket by bracket from the top scoregroup down, for the
 * candidate pairing that best satisfies a strict priority list of criteria
 * ([C1]-[C21]) and, among equally good candidates, the one generated first by
 * the prescribed sequence of S2 transpositions and S1/S2 exchanges. Article 3
 * allows any method that yields the same result.
 *
 * Method: each bracket is decided by maximum-weight matching over the
 * bracket and every lower player (so a complete round-pairing always remains
 * possible, [C4]). Every edge weight packs the criteria as bit fields in
 * priority order; the low-order fields encode the generation order, so the
 * best candidate that is generated first is exactly the maximum. Two solves
 * per bracket: the first fixes the moved-down players' pairs, the second the
 * remainder, whose S1/S2 split depends on the first.
 *
 * The weight design follows the published structure of the reference engines
 * (Gacrux, MIT, copyright FIDE, www.gacrux.no; bbpPairings); the code is our
 * own, and CI compares our pairings with both on random tournaments.
 */

const bitsFor = (x: bigint): bigint => {
  let b = 0n;
  let v = x;
  while (v > 0n) {
    v >>= 1n;
    b++;
  }
  return b;
};

interface Field {
  /** Width in bits, enough for the field's sum over a whole matching. */
  readonly width: bigint;
  readonly value: (h: DutchPlayer, l: DutchPlayer) => bigint;
}

/** Pack fields (most significant first) into one weight per edge. */
function buildEdges(
  vertices: readonly DutchPlayer[],
  fields: readonly Field[],
  allowed: (h: DutchPlayer, l: DutchPlayer) => boolean,
): WeightedEdge[] {
  const edges: WeightedEdge[] = [];
  for (let i = 0; i < vertices.length; i++) {
    const h = vertices[i] as DutchPlayer;
    for (let j = i + 1; j < vertices.length; j++) {
      const l = vertices[j] as DutchPlayer;
      if (!compatible(h, l) || !allowed(h, l)) continue;
      let w = 0n;
      for (const f of fields) w = (w << f.width) + f.value(h, l);
      edges.push({ u: i, v: j, w });
    }
  }
  return edges;
}

interface BracketContext {
  /** Scores (tenths) -> bit position of that scoregroup in score-weighted fields. */
  readonly shift: ReadonlyMap<number, bigint>;
  /** Total width of a score-weighted field. */
  readonly shiftWidth: bigint;
  readonly countWidth: bigint;
  readonly roundsPlayed: number;
  readonly byeScore: number | null;
  readonly singleDownfloaterIsBye: boolean;
  readonly unplayedRank: ReadonlyMap<number, bigint>;
}

const one = (b: boolean): bigint => (b ? 1n : 0n);

/** The criteria fields [C4]-[C21] for the current bracket. */
function criteriaFields(
  ctx: BracketContext,
  inCurrent: (h: DutchPlayer, l: DutchPlayer) => boolean,
  inNext: (h: DutchPlayer, l: DutchPlayer) => boolean,
): Field[] {
  const pow = (score: number): bigint => 1n << (ctx.shift.get(score) as bigint);
  const cw = ctx.countWidth;
  const sw = ctx.shiftWidth;
  const byeCandidate = (p: DutchPlayer): boolean =>
    ctx.byeScore !== null && p.pabEligible && p.score <= ctx.byeScore;
  const fields: Field[] = [
    // [C4] completion and [C5]: every edge counts; matching non-candidates is
    // preferred so the unmatched player (the PAB) is a candidate.
    { width: cw + 2n, value: (h, l) => 1n + one(!byeCandidate(h)) + one(!byeCandidate(l)) },
    // [C6] maximise pairs in the bracket.
    { width: cw, value: (h, l) => one(inCurrent(h, l)) },
    // [C7] minimise downfloater scores: maximise the scores paired.
    { width: sw, value: (h, l) => (inCurrent(h, l) ? pow(h.score) : 0n) },
    // [C8] the next bracket: pairs, then scores paired.
    { width: cw, value: (h, l) => one(inNext(h, l)) },
    { width: sw, value: (h, l) => (inNext(h, l) ? pow(h.score) : 0n) },
    // [C9] minimise the unplayed games of the PAB assignee.
    {
      width: 2n * cw,
      value: (h, l) => {
        if (!ctx.singleDownfloaterIsBye || ctx.byeScore === null) return 0n;
        let v = 0n;
        if (h.score === ctx.byeScore) v += ctx.unplayedRank.get(h.playedGames) ?? 0n;
        if (l.score === ctx.byeScore) v += ctx.unplayedRank.get(l.playedGames) ?? 0n;
        return v;
      },
    },
    // [C10] topscorers (or opponents) with |colour difference| > 2.
    {
      width: cw,
      value: (h, l) =>
        one(
          inCurrent(h, l) &&
            (!h.absoluteImbalance || !l.absoluteImbalance || h.preference !== l.preference),
        ),
    },
    // [C11] topscorers (or opponents) with the same colour three times running.
    {
      width: cw,
      value: (h, l) => {
        if (!inCurrent(h, l)) return 0n;
        if (h.strength !== 'absolute' || l.strength !== 'absolute') return 1n;
        if (h.preference !== l.preference) return 1n;
        if (h.colourDifference === l.colourDifference) {
          return one(h.repeatedColour === null || h.repeatedColour !== l.repeatedColour);
        }
        const loser = Math.abs(h.colourDifference) > Math.abs(l.colourDifference) ? l : h;
        const other = h.preference === 'w' ? 'b' : 'w';
        return one(loser.repeatedColour !== other);
      },
    },
    // [C12] colour preferences granted.
    {
      width: cw,
      value: (h, l) =>
        one(
          inCurrent(h, l) &&
            (h.preference === null || l.preference === null || h.preference !== l.preference),
        ),
    },
    // [C13] strong colour preferences granted.
    {
      width: cw,
      value: (h, l) => {
        if (!inCurrent(h, l)) return 0n;
        const strongish = (p: DutchPlayer): boolean =>
          p.strength === 'strong' || p.strength === 'absolute';
        return one(
          !strongish(h) ||
            !strongish(l) ||
            (h.strength === 'absolute' && l.strength === 'absolute') ||
            h.preference !== l.preference,
        );
      },
    },
  ];
  const floatFields = (back: 1 | 2): Field[] => {
    const f = (p: DutchPlayer): DutchPlayer['float1'] => (back === 1 ? p.float1 : p.float2);
    return [
      // [C14]/[C16] resident downfloaters who downfloated before.
      {
        width: cw,
        value: (h, l) =>
          inCurrent(h, l) ? one(f(l) === 'down') + one(h.score <= l.score && f(h) === 'down') : 0n,
      },
      // [C15]/[C17] MDP opponents who upfloated before.
      {
        width: cw,
        value: (h, l) => one(inCurrent(h, l) && !(h.score > l.score && f(l) === 'up')),
      },
    ];
  };
  const floatScoreFields = (back: 1 | 2): Field[] => {
    const f = (p: DutchPlayer): DutchPlayer['float1'] => (back === 1 ? p.float1 : p.float2);
    return [
      // [C18]/[C20] scores of MDPs who downfloated before.
      {
        width: sw + 1n,
        value: (h, l) =>
          inCurrent(h, l)
            ? (f(l) === 'down' ? pow(l.score) : 0n) + (f(h) === 'down' ? pow(h.score) : 0n)
            : 0n,
      },
      // [C19]/[C21] scores of opponents of MDP upfloaters.
      {
        width: sw,
        value: (h, l) =>
          inCurrent(h, l) && !(f(l) === 'up' && h.score > l.score) ? pow(h.score) : 0n,
      },
    ];
  };
  if (ctx.roundsPlayed >= 1) fields.push(...floatFields(1));
  if (ctx.roundsPlayed >= 2) fields.push(...floatFields(2));
  if (ctx.roundsPlayed >= 1) fields.push(...floatScoreFields(1));
  if (ctx.roundsPlayed >= 2) fields.push(...floatScoreFields(2));
  return fields;
}

function solve(
  vertices: readonly DutchPlayer[],
  edges: readonly WeightedEdge[],
): Map<number, DutchPlayer> {
  const mate = maxWeightMatching(vertices.length, edges);
  const partner = new Map<number, DutchPlayer>();
  for (let i = 0; i < vertices.length; i++) {
    const m = mate[i] as number;
    if (m >= 0) partner.set((vertices[i] as DutchPlayer).id, vertices[m] as DutchPlayer);
  }
  return partner;
}

function scoreShifts(players: readonly DutchPlayer[]): {
  shift: Map<number, bigint>;
  width: bigint;
} {
  const counts = new Map<number, number>();
  for (const p of players) counts.set(p.score, (counts.get(p.score) ?? 0) + 1);
  const scores = [...counts.keys()].sort((a, b) => a - b);
  const shift = new Map<number, bigint>();
  let acc = 0n;
  for (const s of scores) {
    shift.set(s, acc);
    acc += bitsFor(BigInt(2 * (counts.get(s) as number) + 1));
  }
  return { shift, width: acc + 1n };
}

export class DutchEngine implements PairingEngine {
  readonly id = 'dutch';
  readonly name = 'FIDE Dutch (C.04.3, 2026)';

  pair(request: PairingRequest): PairingResult {
    const players = buildDutchPlayers(request);
    const notes: string[] = [];
    if (players.length === 0) return { pairs: [], byes: [], notes };
    if (players.length === 1) {
      const only = players[0] as DutchPlayer;
      if (!only.pabEligible) throw new PairingError('the only player cannot receive the bye');
      return { pairs: [], byes: [only.id], notes };
    }
    const n = players.length;
    const odd = n % 2 === 1;
    const countWidth = bitsFor(BigInt(3 * n + 3));
    const roundsPlayed = request.round - 1;
    const { shift, width: shiftWidth } = scoreShifts(players);
    const topScore = (players[0] as DutchPlayer).score;

    // Requirement zero, [C4] and [C5]: can the round be completed, and from
    // which score does the PAB come?
    let byeScore: number | null = null;
    let singleDownfloaterIsBye = false;
    const unplayedRank = new Map<number, bigint>();
    {
      const fields: Field[] = [
        { width: countWidth + 2n, value: (h, l) => 1n + one(!h.pabEligible) + one(!l.pabEligible) },
        {
          width: bitsFor(BigInt(n)) + 16n,
          value: (h, l) => (shift.get(h.score) as bigint) + (shift.get(l.score) as bigint),
        },
        { width: countWidth, value: (_h, l) => one(l.score >= topScore) },
      ];
      const partner = solve(
        players,
        buildEdges(players, fields, () => true),
      );
      const unmatched = players.filter((p) => !partner.has(p.id));
      if (unmatched.length > (odd ? 1 : 0) || (odd && !(unmatched[0] as DutchPlayer).pabEligible)) {
        throw new PairingError(
          'no pairing satisfies the absolute criteria (C.04.3 1.9.3: the Chief Arbiter decides)',
        );
      }
      if (odd) {
        const bye = unmatched[0] as DutchPlayer;
        byeScore = bye.score;
        if (byeScore >= topScore) {
          singleDownfloaterIsBye = players
            .filter((p) => p.score >= topScore && p !== bye)
            .every((p) => (partner.get(p.id) as DutchPlayer).score >= topScore);
        }
        const counts = players
          .filter((p) => p.score === byeScore)
          .map((p) => p.playedGames)
          .sort((a, b) => b - a);
        counts.forEach((c, i) => unplayedRank.set(c, BigInt(i)));
      }
    }

    const finalPairs: [DutchPlayer, DutchPlayer][] = [];
    const explanation: string[] = [];
    const fmt = (x: number): string => (x / 10).toFixed(1);
    const ids = (ps: readonly DutchPlayer[]): string => ps.map((p) => p.id).join(', ') || 'none';
    if (byeScore !== null) {
      explanation.push(
        `The pairing-allocated bye goes to the ${fmt(byeScore)} scoregroup ([C5]: the lowest score that leaves a legal pairing).`,
      );
    }
    let remaining = [...players];
    let mdps: DutchPlayer[] = [];
    const groupScores = [...new Set(players.map((p) => p.score))].sort((a, b) => b - a);

    for (let g = 0; g < groupScores.length; g++) {
      const s = groupScores[g] as number;
      const nextScore = groupScores[g + 1];
      const residents = remaining.filter((p) => p.score === s && !mdps.includes(p));
      if (residents.length === 0 && mdps.length === 0) continue;
      const residentSet = new Set(residents.map((p) => p.id));
      const nextSet = new Set(
        nextScore === undefined
          ? []
          : remaining.filter((p) => p.score === nextScore).map((p) => p.id),
      );
      const ctx: BracketContext = {
        shift,
        shiftWidth,
        countWidth,
        roundsPlayed,
        byeScore,
        singleDownfloaterIsBye,
        unplayedRank,
      };
      const inCurrent = (_h: DutchPlayer, l: DutchPlayer): boolean => residentSet.has(l.id);
      const inNext = (_h: DutchPlayer, l: DutchPlayer): boolean => nextSet.has(l.id);
      const base = criteriaFields(ctx, inCurrent, inNext);

      // Solve 1: which moved-down players are paired, and with whom
      // (Articles 3.2.2, 4.4: sets of pairable MDPs by smallest differing BSN;
      // 4.2: S2 transpositions for the MDP-pairing).
      const K = mdps.length;
      const R = residents.length;
      const mdpIndex = new Map(mdps.map((p, i) => [p.id, i]));
      const resIndex = new Map(residents.map((p, i) => [p.id, i]));
      const fieldsA: Field[] = [...base];
      if (K > 0) {
        fieldsA.push({
          width: BigInt(K) + countWidth,
          value: (h, l) => {
            const i = mdpIndex.get(h.id);
            return i !== undefined && resIndex.has(l.id) ? 1n << BigInt(K - 1 - i) : 0n;
          },
        });
        const base1 = BigInt(R + 1);
        fieldsA.push({
          width: bitsFor(base1 ** BigInt(K)) + countWidth,
          value: (h, l) => {
            const i = mdpIndex.get(h.id);
            const j = resIndex.get(l.id);
            if (i === undefined || j === undefined) return 0n;
            return BigInt(R - j) * base1 ** BigInt(K - 1 - i);
          },
        });
      }
      const partnerA = solve(
        remaining,
        buildEdges(remaining, fieldsA, () => true),
      );
      const fixed = new Set<number>();
      for (const m of mdps) {
        const p = partnerA.get(m.id);
        if (p !== undefined && resIndex.has(p.id)) {
          finalPairs.push([m, p]);
          fixed.add(m.id);
          fixed.add(p.id);
        }
      }
      const limbo = new Set(mdps.filter((m) => !fixed.has(m.id)).map((m) => m.id));
      const remainder = residents.filter((p) => !fixed.has(p.id));
      let remainderPairs = 0;
      for (const p of remainder) {
        const q = partnerA.get(p.id);
        if (q !== undefined && residentSet.has(q.id) && !fixed.has(q.id) && q.idx < p.idx)
          remainderPairs++;
      }

      // Solve 2: the remainder (Article 3.6: transpositions, then resident
      // exchanges ordered by 4.3.2).
      const pool = remaining.filter((p) => !fixed.has(p.id));
      const RN = remainder.length;
      const RP = remainderPairs;
      const rIndex = new Map(remainder.map((p, i) => [p.id, i]));
      const inRem = (h: DutchPlayer, l: DutchPlayer): boolean =>
        rIndex.has(h.id) && rIndex.has(l.id);
      const baseRN = BigInt(RN + 1);
      const fieldsB: Field[] = [
        ...base,
        // 4.3.2.1 fewest exchanges: pairs whose higher player stays in S1.
        {
          width: countWidth,
          value: (h, l) => one(inRem(h, l) && (rIndex.get(h.id) as number) < RP),
        },
        // 4.3.2.2 smallest difference of exchanged BSN sums.
        {
          width: bitsFor(BigInt(RN * RN + 1)) + 1n,
          value: (h, l) => (inRem(h, l) ? BigInt(RN - (rIndex.get(h.id) as number)) : 0n),
        },
        // 4.3.2.3 largest BSN moved from S1 to S2.
        {
          width: BigInt(RP + 1) + countWidth,
          value: (h, l) => {
            if (!inRem(h, l)) return 0n;
            const i = rIndex.get(h.id) as number;
            return i < RP ? (1n << BigInt(RP)) - (1n << BigInt(i)) : 1n << BigInt(RP);
          },
        },
        // 4.3.2.4 smallest BSN moved from S2 to S1.
        {
          width: BigInt(RN + 1) + countWidth,
          value: (h, l) => {
            if (!inRem(h, l)) return 0n;
            const i = rIndex.get(h.id) as number;
            return i >= RP ? 1n << BigInt(RN - 1 - i) : 0n;
          },
        },
        // 4.2 S2 transpositions: S1 players in order take the smallest BSN.
        {
          width: bitsFor(baseRN ** BigInt(RN)) + countWidth,
          value: (h, l) => {
            if (!inRem(h, l)) return 0n;
            const i = rIndex.get(h.id) as number;
            const j = rIndex.get(l.id) as number;
            return BigInt(RN - j) * baseRN ** BigInt(RN - 1 - i);
          },
        },
      ];
      const partnerB = solve(
        pool,
        buildEdges(pool, fieldsB, (h, l) => !(limbo.has(h.id) && residentSet.has(l.id))),
      );
      for (const p of remainder) {
        const q = partnerB.get(p.id);
        if (q !== undefined && rIndex.has(q.id) && q.idx > p.idx) {
          finalPairs.push([p, q]);
          fixed.add(p.id);
          fixed.add(q.id);
        }
      }

      const downfloaters = [...mdps, ...residents].filter((p) => !fixed.has(p.id));
      {
        const mdpPairs = finalPairs
          .filter(([a, b]) => mdpIndex.has(a.id) || mdpIndex.has(b.id))
          .filter(([a, b]) => resIndex.has(a.id) || resIndex.has(b.id));
        const remPairs = finalPairs.filter(([a, b]) => rIndex.has(a.id) && rIndex.has(b.id));
        const parts = [`Bracket ${fmt(s)}: residents ${ids(residents)}`];
        if (mdps.length > 0) parts.push(`moved-down players ${ids(mdps)}`);
        if (mdpPairs.length > 0)
          parts.push(`MDP pairs ${mdpPairs.map(([a, b]) => `${a.id}-${b.id}`).join(' ')}`);
        if (remPairs.length > 0)
          parts.push(`pairs ${remPairs.map(([a, b]) => `${a.id}-${b.id}`).join(' ')}`);
        parts.push(`downfloaters ${ids(downfloaters)}`);
        if (singleDownfloaterIsBye) parts.push('[C9] applied (fewest unplayed games for the bye)');
        explanation.push(parts.join('; ') + '.');
      }
      remaining = remaining.filter((p) => !fixed.has(p.id));
      mdps = downfloaters.sort((a, b) => a.idx - b.idx);

      // [C9] applies in the next bracket only if it is the PAB's bracket and
      // every carried player is predicted to pair within it.
      singleDownfloaterIsBye =
        odd && nextScore !== undefined && byeScore !== null && byeScore >= nextScore;
      if (singleDownfloaterIsBye && nextScore !== undefined) {
        // Everyone carried into the next bracket (downfloaters and the next
        // scoregroup's residents) must be predicted to pair within it.
        for (const d of [...downfloaters, ...remaining.filter((p) => p.score === nextScore)]) {
          const q = partnerB.get(d.id);
          if (q !== undefined && q.score < nextScore) {
            singleDownfloaterIsBye = false;
            break;
          }
        }
      }
    }

    // Anything left after the last scoregroup: the PAB, or (rarely) players
    // only pairable with each other.
    let byes: number[] = [];
    if (mdps.length > 0) {
      if (mdps.length === 1 && odd) {
        byes = [(mdps[0] as DutchPlayer).id];
      } else {
        const partner = solve(
          mdps,
          buildEdges(
            mdps,
            [
              {
                width: countWidth + 2n,
                value: (h, l) => 1n + one(!h.pabEligible) + one(!l.pabEligible),
              },
            ],
            () => true,
          ),
        );
        const done = new Set<number>();
        for (const p of mdps) {
          const q = partner.get(p.id);
          if (q !== undefined && !done.has(p.id)) {
            finalPairs.push(p.idx < q.idx ? [p, q] : [q, p]);
            done.add(p.id);
            done.add(q.id);
          }
        }
        const left = mdps.filter((p) => !done.has(p.id));
        if (left.length > 1 || (left.length === 1 && !odd)) {
          throw new PairingError('the round-pairing cannot be completed');
        }
        byes = left.map((p) => p.id);
        notes.push('players left after the last scoregroup were paired with each other');
      }
    }

    const ordered = orderBoards(finalPairs, request);
    for (const o of ordered)
      explanation.push(
        `Board ${o.board}: ${o.pair.white}-${o.pair.black}, colours: ${COLOUR_RULE_TEXT[o.rule]} (Art. ${o.rule}).`,
      );
    if (byes.length > 0) explanation.push(`Pairing-allocated bye: ${byes.join(', ')}.`);
    return { pairs: ordered.map((o) => o.pair), byes, notes, explanation };
  }
}

/**
 * Effective pairing numbers for Article 5.2.5: players are numbered in TPN
 * order, skipping those who are absent this round and have never taken part
 * in a pairing (the reference engines number round 1 this way).
 */
export function effectiveNumbers(request: PairingRequest): Map<number, number> {
  const out = new Map<number, number>();
  let n = 0;
  for (const p of [...request.players].sort((a, b) => a.id - b.id)) {
    const participated = p.history.some((e) => e.opponent !== null || e.result === 'U');
    if (p.available || participated) out.set(p.id, ++n);
  }
  return out;
}

/** Colours (Article 5) and board order (C.04.2 3.6). */
function orderBoards(
  pairs: readonly [DutchPlayer, DutchPlayer][],
  request: PairingRequest,
): { board: number; pair: Pair; rule: ColourRule }[] {
  const byId = new Map(request.players.map((p) => [p.id, p]));
  const effective = effectiveNumbers(request);
  const sorted = pairs
    .map(([a, b]) => (a.idx < b.idx ? ([a, b] as const) : ([b, a] as const)))
    .sort(
      ([a1, b1], [a2, b2]) =>
        a2.score - a1.score || a2.score + b2.score - (a1.score + b1.score) || a1.id - a2.id,
    );
  return sorted.map(([higher, lower], i) => {
    const hp = byId.get(higher.id);
    const lp = byId.get(lower.id);
    const { colour: c, rule } = allocateColoursWithRule(
      colourState(hp?.history ?? []),
      colourState(lp?.history ?? []),
      effective.get(higher.id) ?? higher.id,
      request.initialColour,
    );
    const pair =
      c === 'w' ? { white: higher.id, black: lower.id } : { white: lower.id, black: higher.id };
    return { board: i + 1, pair, rule };
  });
}
